/**
 * Glossary tooltip positioning · all viewports (desktop hover added 8 JUN 2026).
 *
 * Originally only phones/tablets used this JS positioner; desktop kept a pure-CSS
 * `:hover` tooltip anchored `bottom:100%; left:50%`. That broke once terms lived
 * inside bordered blocks with `overflow:hidden` (.hint / .drawer / .tracks / the
 * acceptance-forms list): near a block's top/left edge the tooltip was clipped or
 * overlapped the heading above, and the 280px box overflowed the container left.
 *
 * Now BOTH desktop and mobile position the tooltip with `position:fixed` clamped
 * to the viewport — below the term when there's room, otherwise above — so it
 * escapes any ancestor `overflow:hidden` and never leaves the screen or a block.
 * Desktop opens on hover/focus and closes on leave/blur; mobile tap-toggles.
 * Works because `.screen` carries no `transform` (entrance is opacity-only), so
 * `fixed` anchors to the viewport rather than a transformed ancestor.
 *
 * Markup contract (authored content): `<span class="glossary-term">word<span
 * class="glossary-tooltip">…</span></span>`. No `data-*`/aria authored — added
 * here. Visibility is driven by `aria-expanded` on the term (CSS shows the tip
 * only after JS has positioned it, so it never flashes at the un-clamped spot).
 */

const MOBILE_BP = 640;
const TOP_SAFE = 56; // clear the topbar
const BOTTOM_SAFE = 140; // clear BottomTabs (~72) + ticket action-bar (~56) + gap
const EDGE = 12;

export function initGlossary(root: HTMLElement): () => void {
  if (typeof window === 'undefined') return () => {};

  const isMobile = () => window.innerWidth <= MOBILE_BP;
  const cleanups: Array<() => void> = [];
  let openTerm: HTMLElement | null = null;

  function position(term: HTMLElement, tip: HTMLElement): void {
    tip.style.position = 'fixed';
    tip.style.right = 'auto';
    tip.style.bottom = 'auto';
    tip.style.transform = 'none';
    tip.style.maxWidth = `min(320px, calc(100vw - ${EDGE * 2}px))`;
    // Measure after the constraints above are applied (tooltip has layout even
    // while visibility:hidden, so this is safe before we flip aria-expanded).
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const termRect = term.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const w = tipRect.width;
    const h = tipRect.height;

    // Desktop has no bottom tab-bar/action-bar to clear, so reserve only a small
    // gutter; mobile keeps the larger safe-area.
    const bottomSafe = isMobile() ? BOTTOM_SAFE : 24;
    const left = Math.min(Math.max(EDGE, termRect.left), vw - w - EDGE);
    let top = termRect.bottom + 8;
    if (top + h > vh - bottomSafe) {
      const above = termRect.top - h - 8;
      top = above >= TOP_SAFE ? above : Math.max(TOP_SAFE, vh - bottomSafe - h);
    }
    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(Math.max(TOP_SAFE, top))}px`;
  }

  function close(): void {
    if (!openTerm) return;
    openTerm.setAttribute('aria-expanded', 'false');
    const tip = openTerm.querySelector<HTMLElement>('.glossary-tooltip');
    if (tip) tip.style.cssText = ''; // back to CSS default (hidden + absolute)
    openTerm = null;
  }

  const terms = Array.from(root.querySelectorAll<HTMLElement>('.glossary-term'));
  for (const term of terms) {
    const tip = term.querySelector<HTMLElement>('.glossary-tooltip');
    if (!tip) continue;
    if (term.dataset.glossInit === '1') continue;
    term.dataset.glossInit = '1';
    term.setAttribute('tabindex', '0');
    term.setAttribute('role', 'button');
    term.setAttribute('aria-expanded', 'false');

    // Both desktop and mobile now JS-position the tooltip (position:fixed clamped
    // to the viewport, flipped above/below). This is what lets it escape ancestor
    // `overflow:hidden` clipping inside bordered blocks (.hint/.drawer/.tracks…)
    // and never overflow a container edge — the old desktop pure-CSS `:hover`
    // tooltip clipped near the top/left of such blocks. Desktop = hover/focus
    // opens, leave/blur closes. Mobile = tap toggles.
    const open = () => {
      close();
      position(term, tip);
      term.setAttribute('aria-expanded', 'true');
      openTerm = term;
    };
    const toggle = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      const wasOpen = term.getAttribute('aria-expanded') === 'true';
      close();
      if (!wasOpen) open();
    };
    const onClick = (e: Event) => {
      if (isMobile()) toggle(e);
    };
    const onEnter = () => {
      if (!isMobile()) open();
    };
    const onLeave = () => {
      if (!isMobile()) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && isMobile()) toggle(e);
      else if (e.key === 'Escape') close();
    };
    term.addEventListener('click', onClick);
    term.addEventListener('mouseenter', onEnter);
    term.addEventListener('mouseleave', onLeave);
    term.addEventListener('focus', onEnter);
    term.addEventListener('blur', onLeave);
    term.addEventListener('keydown', onKey);
    cleanups.push(() => {
      term.removeEventListener('click', onClick);
      term.removeEventListener('mouseenter', onEnter);
      term.removeEventListener('mouseleave', onLeave);
      term.removeEventListener('focus', onEnter);
      term.removeEventListener('blur', onLeave);
      term.removeEventListener('keydown', onKey);
      // StrictMode-safe: clear the init-guard so a re-run (StrictMode double-invoke
      // or dep change) re-wires the listeners. Without this, run 2 sees glossInit==='1'
      // and skips wiring after run 1's cleanup removed the listeners → tooltip dead.
      // (The pure-CSS `:hover` fallback that used to mask this was removed; visibility
      // is now JS-only via aria-expanded.)
      delete term.dataset.glossInit;
    });
  }

  const onDocClick = (e: MouseEvent) => {
    if (openTerm && e.target instanceof Node && !openTerm.contains(e.target)) close();
  };
  const onScrollOrResize = () => close();
  document.addEventListener('click', onDocClick);
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);
  cleanups.push(() => document.removeEventListener('click', onDocClick));
  cleanups.push(() => window.removeEventListener('scroll', onScrollOrResize));
  cleanups.push(() => window.removeEventListener('resize', onScrollOrResize));
  cleanups.push(close);

  return () => cleanups.forEach((fn) => fn());
}
